import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLANS } from "@/lib/constants";
import { Check, X, Crown, CreditCard, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const { user, setUser } = useAuth();
  const [checkout, setCheckout] = useState(null);
  const [card, setCard] = useState({ number: "", name: "", expiry: "", cvc: "" });
  const [paying, setPaying] = useState(false);
  const [yearly, setYearly] = useState(false);

  const current = user?.plan || "gratuit";

  const subscribe = async (plan) => {
    try {
      const { data } = await api.post("/plan", { plan });
      setUser(data);
      toast.success(plan === "gratuit" ? "Tu es repassé à la formule Gratuite" : `Bienvenue dans la formule ${PLANS[plan].name} !`);setCheckout(null);
      setCard({ number: "", name: "", expiry: "", cvc: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    setPaying(true);
    await new Promise((r) => setTimeout(r, 1200));
    await subscribe(checkout.plan);
    setPaying(false);
  };

  return (
    <div className="space-y-8" data-testid="pricing-page">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">Tarifs & abonnements</h1>
        <p className="text-slate-500 text-sm sm:text-base mt-2">
          Ta formule actuelle : <span className="font-bold text-indigo-600 capitalize" data-testid="pricing-current-plan">{PLANS[current].name}</span>.
          Change quand tu veux, sans engagement.
        </p>
      </div>

      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1 p-1 bg-white border border-slate-200 rounded-full" data-testid="billing-toggle">
          <button
            onClick={() => setYearly(false)}
            data-testid="billing-monthly"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${!yearly ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            Mensuel
          </button>
          <button
            onClick={() => setYearly(true)}
            data-testid="billing-yearly"
            className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors duration-200 flex items-center gap-2 ${yearly ? "bg-indigo-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}
          >
            Annuel
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {Object.entries(PLANS).map(([key, plan]) => {
          const isCurrent = current === key;
          return (
            <div
              key={key}
              data-testid={`pricing-card-${key}`}
              className={`relative bg-white rounded-3xl border p-7 shadow-sm flex flex-col ${
                key === "premium" ? "border-indigo-300 ring-2 ring-indigo-500/20" : "border-slate-200/80"
              } ${isCurrent ? "ring-2 ring-emerald-400/50" : ""}`}
            >
              {key === "premium" && (
                <span className={`absolute -top-3 ${isCurrent ? "left-5" : "left-1/2 -translate-x-1/2"} px-3 py-1 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white text-xs font-bold uppercase tracking-wider flex items-center gap-1`}>
                  <Crown className="h-3 w-3" /> Populaire
                </span>
              )}
              {isCurrent && (
                <span className="absolute -top-3 right-5 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider" data-testid={`pricing-current-badge-${key}`}>
                  Ta formule
                </span>
              )}
              <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{plan.tagline}</p>
              <p className="mt-4">
                <span className="text-4xl font-bold text-slate-900" data-testid={`pricing-price-${key}`}>{yearly && plan.priceYear ? plan.priceYear : plan.price}</span>
                <span className="text-sm text-slate-500 ml-1">{yearly && plan.priceYear ? "/ an" : plan.period}</span>
              </p>
              {yearly && plan.priceYear && (
                <p className="mt-1 text-xs font-semibold text-emerald-600 flex items-center gap-1.5" data-testid={`pricing-savings-${key}`}>
                  {plan.savings}
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`pricing-discount-${key}`}>{plan.discount}</span>
                </p>
              )}
              <ul className="mt-6 space-y-2.5 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
                {plan.missing.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-400">
                    <X className="h-4 w-4 text-slate-300 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => (key === "gratuit" ? subscribe("gratuit") : setCheckout({ plan: key, yearly }))}
                disabled={isCurrent}
                data-testid={`pricing-subscribe-${key}`}
                className={`mt-7 w-full py-3 rounded-full font-semibold text-sm transition-colors duration-200 ${
                  isCurrent
                    ? "bg-slate-100 text-slate-400 cursor-default"
                    : key === "premium"
                    ? "bg-gradient-to-r from-indigo-600 to-violet-600 text-white hover:opacity-90"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
              >
                {isCurrent ? "Formule actuelle" : key === "gratuit" ? "Passer en Gratuit" : "Souscrire"}
              </button>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5" data-testid="pricing-sandbox-note">
        <ShieldCheck className="h-3.5 w-3.5" /> Paiement simulé — aucune somme n'est débitée.
      </p>

      {/* Simulated checkout */}
      {checkout && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" data-testid="checkout-modal">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-7 animate-fade-in-up">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-indigo-600" /> Paiement simulé
              </h2>
              <button onClick={() => setCheckout(null)} data-testid="checkout-close" className="p-2 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <p className="text-sm text-slate-500 mb-5">
              Formule <span className="font-bold text-slate-800">{PLANS[checkout.plan].name}</span> —{" "}
              {checkout.yearly ? `${PLANS[checkout.plan].priceYear} / an` : `${PLANS[checkout.plan].price}${PLANS[checkout.plan].period}`}
              {checkout.yearly && <span className="ml-1 text-emerald-600 font-semibold">({PLANS[checkout.plan].discount})</span>}
            </p>
            <form onSubmit={pay} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Numéro de carte</label>
                <input type="text" required value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} data-testid="checkout-card-number" placeholder="4242 4242 4242 4242" maxLength={19} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Nom sur la carte</label>
                <input type="text" required value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} data-testid="checkout-card-name" placeholder="Prénom Nom" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Expiration</label>
                  <input type="text" required value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} data-testid="checkout-card-expiry" placeholder="MM/AA" maxLength={5} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">CVC</label>
                  <input type="text" required value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} data-testid="checkout-card-cvc" placeholder="123" maxLength={4} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
                </div>
              </div>
              <button type="submit" disabled={paying} data-testid="checkout-pay-button" className="w-full py-3.5 rounded-full bg-gradient-to-r from-indigo-600 to-violet-600 text-white font-semibold text-sm hover:opacity-90 disabled:opacity-60">
                {paying ? "Paiement en cours…" : `Payer ${checkout.yearly ? PLANS[checkout.plan].priceYear : PLANS[checkout.plan].price}`}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
